/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

interface WebhookMetadata {
  last_received_at: any;
  last_event_type: string;
  last_order_id: string | null;
  last_status: 'success' | 'error';
  last_error: string | null;
  total_received: number;
}

export default function PrintfulWebhookStatus() {
  const [webhookData, setWebhookData] = useState<WebhookMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getFirestore();

    console.log('Setting up Printful webhook status listener...');

    const unsubscribe = onSnapshot(
      doc(db, 'sync_metadata', 'printful_webhook'),
      (doc) => {
        console.log('Printful webhook status updated!');
        console.log('Doc exists:', doc.exists());
        console.log('Doc data:', doc.data());

        if (doc.exists()) {
          setWebhookData(doc.data() as WebhookMetadata);
        } else {
          setWebhookData(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to Printful webhook status:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) {
      return 'Never';
    }

    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6c757d'
      }}>
        Loading Printful webhook status...
      </div>
    );
  }

  // No doc at all means this endpoint has never been called successfully
  // or unsuccessfully — either the webhook was never registered with
  // Printful, or Printful has simply had nothing to report yet.
  if (!webhookData) {
    return (
      <div className="sync-status" style={{ color: '#6c757d' }}>
        <strong>Webhook Activity:</strong> No webhook events received yet
      </div>
    );
  }

  const isError = webhookData.last_status === 'error';

  return (
    <div className="sync-status">
      <div>
        <strong>Last Webhook Received:</strong> {formatTimestamp(webhookData.last_received_at)}
        <span
          style={{
            marginLeft: '8px',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 'bold',
            backgroundColor: isError ? '#f8d7da' : '#d4edda',
            color: isError ? '#721c24' : '#155724'
          }}
        >
          {isError ? 'Last call failed' : 'Success'}
        </span>
      </div>
      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
        {webhookData.last_event_type && (
          <span>{webhookData.last_event_type}</span>
        )}
        {webhookData.last_order_id && (
          <span> · Order #{webhookData.last_order_id}</span>
        )}
        {webhookData.total_received !== undefined && (
          <span> · {webhookData.total_received} total received</span>
        )}
      </div>
      {isError && webhookData.last_error && (
        <div style={{ fontSize: '12px', color: '#721c24', marginTop: '2px' }}>
          Error: {webhookData.last_error}
        </div>
      )}
    </div>
  );
}
